<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { TASK_POPOVER_PANEL_CLASS } from './taskPopoverPanelClass';

const props = defineProps({
  modelValue: { type: String, required: true },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const isOpen = ref(false);

// Each status carries its own colour so the board reads at a glance:
// slate = not started, blue = active, amber = waiting, teal = done.
const STATUS_STYLES = {
  todo: {
    pill: 'bg-n-slate-9/10 text-n-slate-11 hover:bg-n-slate-9/20',
    dot: 'bg-n-slate-9',
  },
  in_progress: {
    pill: 'bg-n-blue-9/10 text-n-blue-11 hover:bg-n-blue-9/20',
    dot: 'bg-n-blue-9',
  },
  waiting_customer: {
    pill: 'bg-n-amber-9/10 text-n-amber-11 hover:bg-n-amber-9/20',
    dot: 'bg-n-amber-9',
  },
  done: {
    pill: 'bg-n-teal-9/10 text-n-teal-11 hover:bg-n-teal-9/20',
    dot: 'bg-n-teal-9',
  },
};

const options = computed(() => [
  {
    value: 'todo',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TASKS.STATUS.TODO'),
  },
  {
    value: 'in_progress',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TASKS.STATUS.IN_PROGRESS'),
  },
  {
    value: 'waiting_customer',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TASKS.STATUS.WAITING_CUSTOMER'),
  },
  {
    value: 'done',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TASKS.STATUS.DONE'),
  },
]);

const activeLabel = computed(
  () =>
    options.value.find(option => option.value === props.modelValue)?.label ||
    props.modelValue
);

const activeStyle = computed(
  () => STATUS_STYLES[props.modelValue] || STATUS_STYLES.todo
);

const select = value => {
  emit('update:modelValue', value);
  isOpen.value = false;
};
</script>

<template>
  <OnClickOutside @trigger="isOpen = false">
    <div class="relative w-fit">
      <button
        type="button"
        class="inline-flex items-center gap-1.5 rounded-full pl-2 pr-1.5 py-1 text-xs font-medium transition-colors whitespace-nowrap max-w-full"
        :class="activeStyle.pill"
        @click="isOpen = !isOpen"
      >
        <span
          class="size-1.5 rounded-full flex-shrink-0"
          :class="activeStyle.dot"
        />
        <span class="truncate">{{ activeLabel }}</span>
        <Icon icon="i-lucide-chevron-down" size="size-3 flex-shrink-0" />
      </button>
      <div
        v-if="isOpen"
        class="absolute z-30 top-full mt-1 ltr:left-0 rtl:right-0 flex flex-col gap-0.5 p-1 w-40"
        :class="TASK_POPOVER_PANEL_CLASS"
      >
        <button
          v-for="option in options"
          :key="option.value"
          type="button"
          class="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-n-slate-12 hover:bg-n-alpha-2 text-left"
          @click="select(option.value)"
        >
          <span
            class="size-1.5 rounded-full flex-shrink-0"
            :class="STATUS_STYLES[option.value].dot"
          />
          <span class="flex-1 truncate">{{ option.label }}</span>
          <Icon
            v-if="option.value === modelValue"
            icon="i-lucide-check"
            size="size-3 text-n-slate-11"
          />
        </button>
      </div>
    </div>
  </OnClickOutside>
</template>
